import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { impersonateLogin } from 'actions/auth';

/**
 * AdminImpersonate component handles the impersonation login flow.
 * It reads user data from the URL hash fragment, clears the hash for security,
 * stores the token, and redirects to the home page.
 */
const AdminImpersonate = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(true);

    useEffect(() => {
        const processImpersonation = async () => {
            try {
                // Get the hash fragment (without the leading #)
                const hash = window.location.hash.substring(1);

                if (!hash) {
                    setError('No impersonation data provided');
                    setProcessing(false);
                    return;
                }

                // Clear the hash immediately for security (so it's not visible in URL or history)
                window.history.replaceState(null, '', window.location.pathname);

                // Decode the base64 data
                let userData;
                try {
                    userData = JSON.parse(atob(hash));
                } catch (e) {
                    setError('Invalid impersonation data');
                    setProcessing(false);
                    return;
                }

                // Validate required fields
                if (!userData.token || !userData.email) {
                    setError('Missing required impersonation fields');
                    setProcessing(false);
                    return;
                }

                // Dispatch the impersonate login action
                dispatch(impersonateLogin(userData));

                // Redirect to home page
                navigate('/', { replace: true });
            } catch (err) {
                console.error('Impersonation error:', err);
                setError('Failed to process impersonation');
                setProcessing(false);
            }
        };

        processImpersonation();
    }, [dispatch, navigate]);

    if (error) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: '2rem',
                textAlign: 'center'
            }}>
                <h2 style={{ marginBottom: '1rem', color: '#dc2626' }}>Impersonation Error</h2>
                <p style={{ marginBottom: '2rem', color: '#6b7280' }}>{error}</p>
                <button
                    onClick={() => navigate('/')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        fontSize: '1rem'
                    }}
                >
                    Go to Home
                </button>
            </div>
        );
    }

    if (processing) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: '2rem',
                textAlign: 'center'
            }}>
                <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #3b82f6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '1rem'
                }} />
                <p style={{ color: '#6b7280' }}>Setting up impersonation session...</p>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return null;
};

export default AdminImpersonate;
